import type { CsvImportResponse, ListResponse } from '@shared/schemas';

import api from '@/shared/api/api-client';

import { fileImportApiHttp } from '../../_shared/infrastructure/file-import-api.http';
import { licenceQueryHttp } from '../../_shared/infrastructure/licence-query.http';
import { listImportQueryHttp } from '../../_shared/infrastructure/list-import-query.http';
import { profileQueryHttp } from '../../_shared/infrastructure/profile-query.http';
import type { AddListGateway } from './add-list.gateway';

/**
 * Real implementation over the shared api client. This is the single place that
 * knows the Ad List write endpoints and request shapes (kept 1:1 with the
 * original AddList component); the profile-selection reads come from the shared
 * `profileQueryHttp`. `api.post` unwraps the `{ data }` envelope for us.
 */
export const httpAddListGateway: AddListGateway = {
  createList: (draft) =>
    api.post<ListResponse>('/lists', {
      name: draft.name.trim(),
      start_date: draft.startDate,
      end_date: draft.endDate,
      scope: draft.scope.trim(),
      client_scope_description: draft.clientScopeDescription.trim(),
      status: 'draft',
      ...(draft.licenceId ? { licence_id: draft.licenceId } : {}),
    }),

  importFile: async (listId, file) => {
    const payload: Record<string, unknown> = {
      list_id: listId,
      column_mapping: file.columnMapping,
      rows: file.rows,
    };
    if (file.isEnriched) payload.is_enriched = true;
    if (file.overwriteRows.length > 0) payload.overwrite_rows = file.overwriteRows;
    const res = await api.post<CsvImportResponse>('/csv/import', payload);
    return res.created_profile_ids;
  },

  associateProfiles: async (listId, profileIds) => {
    await api.post(`/lists/${listId}/profiles`, { profile_ids: profileIds });
  },

  queueEnrichment: async (listId, licenceId, targetIds) => {
    await api.post('/enrichment-sessions', {
      type: 'linkedin_standard',
      list_id: listId,
      ...(licenceId ? { licence_id: licenceId } : {}),
      target_ids: targetIds,
    });
  },

  // ── Profile-selection data layer (shared with Edit) ──
  ...profileQueryHttp,

  // ── File parse/analyze layer (shared with Edit and the file-import feature) ──
  ...fileImportApiHttp,

  // ── Licence dropdown read (shared with Edit) ──
  ...licenceQueryHttp,

  // ── Import-from-list step reads (shared with Edit) ──
  ...listImportQueryHttp,
};
